import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdvancedSearch.css';
import { supabase } from '../../lib/supabaseClient.ts';
import { Sliders } from 'lucide-react';

const AdvancedSearch = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [petType, setPetType] = useState('all'); // all, cat, dog, rongeur, oiseaux

  const navigate = useNavigate();
  const searchRef = useRef(null);
// Juste après fetchCategories, ajoutez cette fonction de debug
const debugProductCategories = async () => {
  const { data } = await supabase
    .from('products')
    .select('category, subcategory')
    .limit(10);
  
  console.log('🔍 CATEGORIES DANS PRODUCTS:', [...new Set(data?.map(p => p.category))]);
  console.log('🔍 SUBCATEGORIES DANS PRODUCTS:', [...new Set(data?.map(p => p.subcategory))]);
};

// Appelez-le dans le premier useEffect
useEffect(() => {
  if (isOpen) {
    fetchCategories();
    fetchBrands();
    debugProductCategories(); // ✅ AJOUTEZ CETTE LIGNE
  }
}, [isOpen, petType]);
  // Fetch subcategories based on selected categories or petType
  const fetchSubcategories = async () => {
    try {
      let query = supabase.from('subcategories').select('*');

      // Priority 1: Filter by selected categories if any
      if (selectedCategories.length > 0) {
        query = query.in('category_id', selectedCategories);
      }
      // Priority 2: Filter by petType if no categories selected
      else if (petType !== 'all' && categories.length > 0) {
        const catIdsForPet = categories
          .filter(c => c.pet_type === petType)
          .map(c => c.id);
        if (catIdsForPet.length > 0) {
          query = query.in('category_id', catIdsForPet);
        }
      }

      const { data, error } = await query.order('order', { ascending: true });

      if (!error) {
        setSubcategories(data || []);
      } else {
        console.error('Error fetching subcategories:', error);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  // Trigger fetchSubcategories when dependencies change
  useEffect(() => {
    if (categories.length > 0) {
      fetchSubcategories();
    }
  }, [selectedCategories, petType, categories]);

  // Main search/filter effect
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchProducts();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [searchQuery, selectedCategories, selectedSubcategories, selectedBrands, petType, isOpen]);

  // Fetch initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchBrands();
    }
  }, [isOpen]);

  // Refetch categories when pet type changes
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      setSelectedCategories([]);
      setSelectedSubcategories([]);
    }
  }, [petType]);

  // Fetch categories based on pet type
  const fetchCategories = async () => {
    try {
      let query = supabase.from('categories').select('*');

      if (petType !== 'all') {
        query = query.eq('pet_type', petType);
      }

      const { data, error } = await query.order('order', { ascending: true });

      if (!error) {
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch all unique brands
  const fetchBrands = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('brand')
        .not('brand', 'is', null);

      if (!error && data) {
        const uniqueBrands = [...new Set(data.map(p => p.brand).filter(Boolean))].sort();
        setBrands(uniqueBrands);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  // Main search function with comprehensive filtering
const searchProducts = async () => {
  setLoading(true);

  try {
    const productIds = new Set();
    const searchTerm = searchQuery.trim().toLowerCase();
    
    // Check if ANY filter is active
    const hasFilters = searchTerm || 
                       selectedCategories.length > 0 || 
                       selectedSubcategories.length > 0 || 
                       selectedBrands.length > 0 ||
                       petType !== 'all';

    // If no filters at all, show random products
    if (!hasFilters) {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .limit(50)
        .order('created_at', { ascending: false });

      if (!error && data) {
        const shuffled = data.sort(() => 0.5 - Math.random());
        setFilteredProducts(shuffled.slice(0, 20));
      }
      setLoading(false);
      return;
    }

    // ------------------------------
    // STEP 1: Search in products table
    // ------------------------------
    let mainQuery = supabase.from('products').select('*');

    // Search multiple fields ONLY if search term exists
    if (searchTerm) {
      mainQuery = mainQuery.or(
        `name.ilike.%${searchTerm}%,` +
        `description.ilike.%${searchTerm}%,` +
        `brand.ilike.%${searchTerm}%,` +
        `category.ilike.%${searchTerm}%,` +
        `subcategory.ilike.%${searchTerm}%,` +
        `weight.ilike.%${searchTerm}%,` +
        `price_range.ilike.%${searchTerm}%,` +
        `composition.ilike.%${searchTerm}%,` +
        `ingredients.ilike.%${searchTerm}%,` +
        `analysis.ilike.%${searchTerm}%,` +
        `antioxidants.ilike.%${searchTerm}%,` +
        `energy.ilike.%${searchTerm}%`
      );
    }

    const { data: allProducts, error: allError } = await mainQuery.limit(500);
    
    if (allError) {
      console.error('❌ Error fetching products:', allError);
    }

    // Filter products manually based on category/subcategory/brand
    let filteredResults = allProducts || [];

    // Category filter (works with or without search term)
    if (selectedCategories.length > 0) {
      const categoryNames = categories
        .filter(c => selectedCategories.includes(c.id))
        .map(c => c.name);
      
      console.log('🔍 Selected category names:', categoryNames);
      
      filteredResults = filteredResults.filter(product => {
        try {
          const productCategories = JSON.parse(product.category || '[]');
          return categoryNames.some(catName => 
            productCategories.some(pCat => 
              pCat.toLowerCase().trim() === catName.toLowerCase().trim()
            )
          );
        } catch (e) {
          return false;
        }
      });
    } else if (petType !== 'all') {
      const categoryNames = categories
        .filter(c => c.pet_type === petType)
        .map(c => c.name);
      
      console.log('🐾 PetType category names:', categoryNames);
      
      filteredResults = filteredResults.filter(product => {
        try {
          const productCategories = JSON.parse(product.category || '[]');
          return categoryNames.some(catName => 
            productCategories.some(pCat => 
              pCat.toLowerCase().trim() === catName.toLowerCase().trim()
            )
          );
        } catch (e) {
          return false;
        }
      });
    }

    // Subcategory filter (works with or without search term)
    if (selectedSubcategories.length > 0) {
      const subcategoryNames = subcategories
        .filter(s => selectedSubcategories.includes(s.id))
        .map(s => s.name);
      
      console.log('🔍 Selected subcategory names:', subcategoryNames);
      
      filteredResults = filteredResults.filter(product => {
        try {
          const productSubcategories = JSON.parse(product.subcategory || '[]');
          return subcategoryNames.some(subName => 
            productSubcategories.some(pSub => 
              pSub.toLowerCase().trim() === subName.toLowerCase().trim()
            )
          );
        } catch (e) {
          return false;
        }
      });
    }

    // Brand filter (works with or without search term)
    if (selectedBrands.length > 0) {
      console.log('🏷️ Selected brands:', selectedBrands);
      filteredResults = filteredResults.filter(product => 
        selectedBrands.includes(product.brand)
      );
    }

    console.log('📦 Filtered products:', filteredResults.length);

    filteredResults.forEach(p => productIds.add(p.id));

    // ------------------------------
    // STEP 2: Search in product_variations table (ONLY if search term exists)
    // ------------------------------
    if (searchTerm) {
      const { data: variations, error: varError } = await supabase
        .from('product_variations')
        .select('product_id')
        .or(
          `variation_name.ilike.%${searchTerm}%,` +
          `weight.ilike.%${searchTerm}%,` +
          `composition.ilike.%${searchTerm}%,` +
          `price.ilike.%${searchTerm}%,` +
          `ingredients.ilike.%${searchTerm}%,` +
          `analysis.ilike.%${searchTerm}%,` +
          `antioxidants.ilike.%${searchTerm}%,` +
          `energy.ilike.%${searchTerm}%`
        );

      if (varError) {
        console.error('❌ Error in variations search:', varError);
      }

      if (variations && variations.length > 0) {
        console.log('🔄 Variations found:', variations.length);
        
        // Fetch products for these variations and apply filters
        const { data: varProducts } = await supabase
          .from('products')
          .select('*')
          .in('id', variations.map(v => v.product_id));

        if (varProducts) {
          let filteredVarProducts = varProducts;

          // Apply category filter
          if (selectedCategories.length > 0) {
            const categoryNames = categories
              .filter(c => selectedCategories.includes(c.id))
              .map(c => c.name);
            
            filteredVarProducts = filteredVarProducts.filter(product => {
              try {
                const productCategories = JSON.parse(product.category || '[]');
                return categoryNames.some(catName => 
                  productCategories.some(pCat => 
                    pCat.toLowerCase().trim() === catName.toLowerCase().trim()
                  )
                );
              } catch (e) {
                return false;
              }
            });
          } else if (petType !== 'all') {
            const categoryNames = categories
              .filter(c => c.pet_type === petType)
              .map(c => c.name);
            
            filteredVarProducts = filteredVarProducts.filter(product => {
              try {
                const productCategories = JSON.parse(product.category || '[]');
                return categoryNames.some(catName => 
                  productCategories.some(pCat => 
                    pCat.toLowerCase().trim() === catName.toLowerCase().trim()
                  )
                );
              } catch (e) {
                return false;
              }
            });
          }

          // Apply subcategory filter
          if (selectedSubcategories.length > 0) {
            const subcategoryNames = subcategories
              .filter(s => selectedSubcategories.includes(s.id))
              .map(s => s.name);
            
            filteredVarProducts = filteredVarProducts.filter(product => {
              try {
                const productSubcategories = JSON.parse(product.subcategory || '[]');
                return subcategoryNames.some(subName => 
                  productSubcategories.some(pSub => 
                    pSub.toLowerCase().trim() === subName.toLowerCase().trim()
                  )
                );
              } catch (e) {
                return false;
              }
            });
          }

          // Apply brand filter
          if (selectedBrands.length > 0) {
            filteredVarProducts = filteredVarProducts.filter(product => 
              selectedBrands.includes(product.brand)
            );
          }

          filteredVarProducts.forEach(p => productIds.add(p.id));
        }
      }
    }

    console.log('🎯 Total unique product IDs:', productIds.size);

    // ------------------------------
    // STEP 3: Fetch final products
    // ------------------------------
    if (productIds.size > 0) {
      const { data: finalProducts, error: finalError } = await supabase
        .from('products')
        .select('*')
        .in('id', Array.from(productIds))
        .order('created_at', { ascending: false });

      console.log('✅ Final products:', finalProducts?.length || 0);

      if (finalError) {
        console.error('❌ Error fetching final products:', finalError);
      }
      
      setFilteredProducts(finalProducts || []);
    } else {
      console.log('⚠️ No results');
      setFilteredProducts([]);
    }

  } catch (error) {
    console.error('❌ Error searching products:', error);
    setFilteredProducts([]);
  } finally {
    setLoading(false);
  }
};
  const handleProductClick = (productId) => {
    navigate(`/shop-details/${productId}`);
    onClose();
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
    setSelectedSubcategories([]);
  };

  const toggleSubcategory = (subcategoryId) => {
    setSelectedSubcategories(prev =>
      prev.includes(subcategoryId)
        ? prev.filter(id => id !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedBrands([]);
    setPetType('all');
    setSearchQuery('');
  };
const uniqueCategories = React.useMemo(() => {
  const seen = new Set();
  return categories.filter(cat => {
    if (seen.has(cat.id)) return false;
    seen.add(cat.id);
    return true;
  });
}, [categories]);

const uniqueSubcategories = React.useMemo(() => {
  const seen = new Set();
  return subcategories.filter(sub => {
    if (seen.has(sub.id)) return false;
    seen.add(sub.id);
    return true;
  });
}, [subcategories]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="search-backdrop" onClick={onClose} />

      {/* Advanced Search Modal */}
      <div className="advanced-search-modal">
        {/* Header */}
        <div className="search-header">
          <h2></h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Main Search Input */}
        <div className="search-input-container">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (searchQuery.trim()) {
                const params = new URLSearchParams();
                params.set('q', searchQuery);

                if (petType !== 'all') {
                  params.set('petType', petType);
                }
                if (selectedCategories.length > 0) {
                  params.set('categories', selectedCategories.join(','));
                }
                if (selectedSubcategories.length > 0) {
                  params.set('subcategories', selectedSubcategories.join(','));
                }
                if (selectedBrands.length > 0) {
                  params.set('brands', selectedBrands.join(','));
                }

                navigate(`/search-results?${params.toString()}`);
                onClose();
              }
            }}
            style={{ width: '100%' }}
          >
            <input
              ref={searchRef}
              type="text"
              className="search-input-advanced"
              placeholder="Rechercher par nom, marque, description, poids, goût..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </form>
        </div>

        {/* Pet Type Tabs */}
        <div className="pet-type-tabs">
          <button
            className={`pet-tab ${petType === 'all' ? 'active' : ''}`}
            onClick={() => setPetType('all')}
          >
            Tous
          </button>
          <button
            className={`pet-tab ${petType === 'cat' ? 'active' : ''}`}
            onClick={() => setPetType('cat')}
          >
            🐱 Chats
          </button>
          <button
            className={`pet-tab ${petType === 'dog' ? 'active' : ''}`}
            onClick={() => setPetType('dog')}
          >
            🐕 Chiens
          </button>
          <button
            className={`pet-tab ${petType === 'rongeur' ? 'active' : ''}`}
            onClick={() => setPetType('rongeur')}
          >
            🐭 Rongeurs
          </button>
          <button
            className={`pet-tab ${petType === 'oiseaux' ? 'active' : ''}`}
            onClick={() => setPetType('oiseaux')}
          >
            🐦 Oiseaux
          </button>
        </div>

        {/* Content Area */}
        <div className="search-content">
          {/* Left: Results */}
          <div className="search-results-section">
            <div className="results-header">
              <h3>
                {loading ? 'Recherche...' : `${filteredProducts.length} produit(s) trouvé(s)`}
              </h3>
              {(selectedCategories.length > 0 || selectedSubcategories.length > 0 ||
                selectedBrands.length > 0 || searchQuery) && (
                  <button className="clear-filters-btn" onClick={clearAllFilters}>
                    Effacer les filtres
                  </button>
                )}
            </div>

            <div className="products-grid">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Chargement...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <p>Aucun produit trouvé</p>
                  <p className="empty-subtitle">Essayez d'ajuster vos filtres ou votre recherche</p>
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-card-search"
                    onClick={() => handleProductClick(product.id)}
                  >
                    <div className="product-image-wrapper">
                      {product.image ? (
                        <img src={product.image} alt={product.name} />
                      ) : (
                        <div className="no-image">No image</div>
                      )}
                      {product.promo && (
                        <span className="promo-badge">
                          -{product.promo_percentage || 10}%
                        </span>
                      )}
                      {product.new && <span className="new-badge">Nouveau</span>}
                      {product.best && <span className="best-badge">⭐ Meilleur</span>}
                    </div>

                    <div className="product-info-search">
                      <p className="product-brand">{product.brand || 'Marque inconnue'}</p>
                      <h4 className="product-name">{product.name || 'Produit inconnu'}</h4>
                      <p className="product-description">
                        {product.description || 'Aucune description disponible'}
                      </p>

                      {product.stock_status && (
                        <div className="stock-status">
                          <span className={`stock-badge ${product.stock_status.toLowerCase()}`}>
                            {product.stock_status === 'in_stock' && '✓ En stock'}
                            {product.stock_status === 'out_of_stock' && '✕ Rupture de stock'}
                            {product.stock_status === 'low_stock' && '⚠ Stock limité'}
                          </span>
                        </div>
                      )}

                      <div className="product-price-section">
                        {product.promo && product.price_range ? (
                          <div className="price-with-promo">
                            <span className="old-price">
                              {new Intl.NumberFormat("fr-FR", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                              }).format(parseFloat(product.price_range))}{" "}
                              DT
                            </span>

                            <span className="current-price">
                              {new Intl.NumberFormat("fr-FR", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                              }).format(
                                parseFloat(product.price_range) *
                                (1 - (product.promo_percentage || 10) / 100)
                              )}{" "}
                              DT
                            </span>
                          </div>
                        ) : product.price_range ? (
                          <span className="current-price-solo">
                            {new Intl.NumberFormat("fr-FR", {
                              minimumFractionDigits: 3,
                              maximumFractionDigits: 3,
                            }).format(parseFloat(product.price_range))}{" "}
                            DT
                          </span>
                        ) : (
                          <span className="current-price-solo">Prix indisponible</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
{/* Mobile Filter Toggle */}
{window.innerWidth < 768 && (
  <div className="mobile-filters-toggle lg:hidden flex justify-end mb-2 px-4">
    <button
      onClick={() => setShowFilters(prev => !prev)}
      className={`filter-icon-slide bg-pink-500 text-black p-3 rounded-full shadow-lg flex items-center justify-center  transition`}
    >
      <Sliders size={20} />
    </button>
  </div>
)}

          {/* Right: Filters */}
  {(showFilters || window.innerWidth > 768) && (
<div className={`filters-sidebar ${showFilters ? 'visible' : 'hidden'}`}>
    <h3 className="sidebar-title">Filtres</h3>

            {/* Categories Filter */}
 {uniqueCategories.length > 0 && (
  <div className="filter-section">
    <h4 className="filter-title">
      Catégories ({uniqueCategories.length})
    </h4>
    <div className="filter-options">
      {uniqueCategories.map((category) => (
        <label key={category.id} className="filter-checkbox">
          <input
            type="checkbox"
            checked={selectedCategories.includes(category.id)}
            onChange={() => toggleCategory(category.id)}
          />
          <span className="checkbox-custom"></span>
          <span className="filter-label">{category.name}</span>
        </label>
      ))}
    </div>
  </div>
)}


            {/* Subcategories Filter */}
{uniqueSubcategories.length > 0 && (
  <div className="filter-section">
    <h4 className="filter-title">
      Sous-catégories ({uniqueSubcategories.length})
    </h4>

    <div className="filter-options">
      {uniqueSubcategories.map((subcategory) => (
        <label key={subcategory.id} className="filter-checkbox">
          <input
            type="checkbox"
            checked={selectedSubcategories.includes(subcategory.id)}
            onChange={() => toggleSubcategory(subcategory.id)}
          />
          <span className="checkbox-custom"></span>
          <span className="filter-label">{subcategory.name}</span>
        </label>
      ))}
    </div>
  </div>
)}


            {/* Brands Filter */}
            {brands.length > 0 && (
              <div className="filter-section">
                <h4 className="filter-title">Marques ({brands.length})</h4>
                <div className="filter-options scrollable">
                  {brands.map((brand) => (
                    <label key={brand} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="filter-label">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdvancedSearch;